import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withCtx as _withCtx, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "space-y-5" }
const _hoisted_2 = { class: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" }
const _hoisted_3 = {
  key: 0,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
}
const _hoisted_4 = {
  key: 1,
  class: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl border border-red-200 dark:border-red-800"
}
const _hoisted_5 = {
  key: 2,
  class: "text-center py-16 text-slate-500 dark:text-slate-400"
}
const _hoisted_6 = { class: "text-sm mt-1" }
const _hoisted_7 = {
  key: 3,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
}
const _hoisted_8 = { class: "flex items-start justify-between gap-2 mb-2" }
const _hoisted_9 = { class: "font-semibold text-base text-gray-800 dark:text-gray-200 truncate" }
const _hoisted_10 = { class: "text-sm text-gray-500 dark:text-gray-400 mb-4 flex-1" }
const _hoisted_11 = { class: "flex items-center gap-2 pt-4 border-t border-gray-100 dark:border-gray-700" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "text-sm text-slate-600 dark:text-slate-300" }
const _hoisted_17 = { class: "font-semibold text-slate-700 dark:text-slate-200" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "space-y-4" }
const _hoisted_20 = { class: "h-11 w-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200" }
const _hoisted_21 = { class: "min-w-0 flex-1 overflow-hidden" }
const _hoisted_22 = ["title"]
const _hoisted_23 = {
  key: 0,
  class: "error-message"
}
const _hoisted_24 = ["disabled"]
const _hoisted_25 = { class: "space-y-4" }
const _hoisted_26 = { class: "h-11 w-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200" }
const _hoisted_27 = { class: "min-w-0 flex-1 overflow-hidden" }
const _hoisted_28 = ["title"]
const _hoisted_29 = {
  key: 0,
  class: "error-message"
}
const _hoisted_30 = ["disabled"]

import { ref, computed, onMounted } from 'vue'
import { FilePlusCorner, Eye, Trash, Upload, FileText, Download as DownloadIcon, Pencil } from 'lucide-vue-next'
import { employeeDocumentService, type EmployeeDocument } from '@/services/employeeDocumentService'
import Modal from '@/components/layout/Modal.vue'
import DocumentDetailsModal from '@/components/documents/DocumentDetailsModal.vue'
import TableControls from '@/components/layout/TableControls.vue'
import { useToast } from 'vue-toastification'

const toastTimeout = 5000


export default /*@__PURE__*/_defineComponent({
  __name: 'ProfileDocumentsTab',
  setup(__props) {

const toast = useToast()
const documents = ref<EmployeeDocument[]>([])
const loading = ref(false)
const error = ref('')
const showDeleteModal = ref(false)
const deleteLoading = ref(false)
const pendingDeleteId = ref<number | null>(null)
const pendingDeleteTitle = ref('')

const searchQuery = ref('')
const statusFilter = ref('')
const statusOptions = [
  { value: '', label: 'All' },
]

const showUploadModal = ref(false)
const uploadTitle = ref('')
const uploadFile = ref<File | null>(null)
const uploadError = ref('')
const uploading = ref(false)
const dragOver = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)

const showViewModal = ref(false)
const selectedDocument = ref<EmployeeDocument | null>(null)
const showEditModal = ref(false)
const editTitle = ref('')
const editFile = ref<File | null>(null)
const editError = ref('')
const editLoading = ref(false)
const editDragOver = ref(false)
const editFileInputRef = ref<HTMLInputElement | null>(null)
const editingDocId = ref<number | null>(null)

const selectedFileName = computed(() => uploadFile.value?.name || '')

const filteredDocuments = computed(() => {
  if (!searchQuery.value.trim()) return documents.value
  const q = searchQuery.value.toLowerCase()
  return documents.value.filter(d => (d.title || '').toLowerCase().includes(q))
})

const fetchDocuments = async () => {
  loading.value = true
  error.value = ''
  try {
    documents.value = await employeeDocumentService.fetchAll()
  } catch {
    error.value = 'Failed to load your documents.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchDocuments()
})

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

const onDrop = (e: DragEvent) => {
  dragOver.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) uploadFile.value = file
}

const openEditModal = (doc: EmployeeDocument) => {
  editingDocId.value = doc.id
  editTitle.value = doc.title
  editFile.value = null
  editError.value = ''
  showEditModal.value = true
}

const onEditDrop = (e: DragEvent) => {
  editDragOver.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) editFile.value = file
}

const onEditFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  editFile.value = target.files?.[0] || null
}

const confirmEdit = async () => {
  if (!editingDocId.value) return
  if (!editTitle.value.trim()) {
    editError.value = 'Title is required.'
    return
  }

  const formData = new FormData()
  formData.append('title', editTitle.value.trim())
  if (editFile.value) {
    formData.append('file', editFile.value)
  }

  editLoading.value = true
  editError.value = ''
  try {
    await employeeDocumentService.update(editingDocId.value, formData)
    toast.success('Document updated successfully', { timeout: toastTimeout })
    showEditModal.value = false
    fetchDocuments()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    editError.value = axiosErr.response?.data?.message || 'Failed to update document.'
  } finally {
    editLoading.value = false
  }
}

const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  uploadFile.value = target.files?.[0] || null
}

const uploadDocument = async () => {
  uploadError.value = ''
  if (!uploadTitle.value.trim()) {
    uploadError.value = 'Title is required.'
    return
  }
  if (!uploadFile.value) {
    uploadError.value = 'Please select a file.'
    return
  }

  const formData = new FormData()
  formData.append('title', uploadTitle.value.trim())
  formData.append('file', uploadFile.value)

  uploading.value = true
  try {
    await employeeDocumentService.create(formData)
    toast.success('Document uploaded successfully', { timeout: toastTimeout })
    showUploadModal.value = false
    uploadTitle.value = ''
    uploadFile.value = null
    fetchDocuments()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    uploadError.value = axiosErr.response?.data?.message || 'Failed to upload document.'
  } finally {
    uploading.value = false
  }
}

const viewDocument = (doc: EmployeeDocument) => {
  selectedDocument.value = doc
  showViewModal.value = true
}

const downloadDocument = async (doc: EmployeeDocument) => {
  const loadingToast = toast.info('Preparing document for download...', { timeout: false })
  try {
    const token = localStorage.getItem('token')
    const url = employeeDocumentService.getDownloadUrl(doc.id)
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Invalid File')
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = doc.title || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(blobUrl)
    document.body.removeChild(a)
    toast.dismiss(loadingToast)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
  } catch {
    toast.dismiss(loadingToast)
    toast.error('Failed to download document', { timeout: toastTimeout })
  }
}

const handleDelete = (id: number) => {
  const doc = documents.value.find(d => d.id === id)
  pendingDeleteId.value = id
  pendingDeleteTitle.value = doc?.title || 'this document'
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (pendingDeleteId.value === null) return
  deleteLoading.value = true
  try {
    await employeeDocumentService.delete(pendingDeleteId.value)
    toast.success('Document deleted', { timeout: toastTimeout })
    showDeleteModal.value = false
    pendingDeleteId.value = null
    fetchDocuments()
  } catch {
    toast.error('Failed to delete document', { timeout: toastTimeout })
  } finally {
    deleteLoading.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[18] || (_cache[18] = _createElementVNode("p", { class: "text-sm text-slate-500 dark:text-slate-400" }, " Upload and manage your important documents. ", -1)),
      _createElementVNode("button", {
        type: "button",
        class: "inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-semibold text-white bg-primary hover:bg-secondary shadow-md transition w-full sm:w-auto",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (showUploadModal.value = true))
      }, [
        _createVNode(_unref(FilePlusCorner), { class: "h-5 w-5" }),
        _cache[17] || (_cache[17] = _createTextVNode(" Upload Document ", -1))
      ])
    ]),
    _createVNode(TableControls, {
      search: searchQuery.value,
      "onUpdate:search": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
      status: statusFilter.value,
      "onUpdate:status": _cache[2] || (_cache[2] = ($event: any) => ((statusFilter).value = $event)),
      placeholder: "Search by title...",
      options: statusOptions
    }, null, 8, ["search", "status"]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (n) => {
            return _createElementVNode("div", {
              key: n,
              class: "bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-5 animate-pulse"
            }, [...(_cache[19] || (_cache[19] = [
              _createStaticVNode("<div class=\"h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3\"></div><div class=\"h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4\"></div><div class=\"flex gap-2 pt-4 border-t border-gray-100 dark:border-gray-700\"><div class=\"h-9 w-9 bg-gray-200 dark:bg-gray-700 rounded-lg\"></div><div class=\"h-9 w-9 bg-gray-200 dark:bg-gray-700 rounded-lg\"></div></div>", 3)
            ]))])
          }), 64))
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(error.value), 1))
        : (filteredDocuments.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(FileText), { class: "h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" }),
              _cache[20] || (_cache[20] = _createElementVNode("p", { class: "text-base font-medium" }, "No documents found", -1)),
              _createElementVNode("p", _hoisted_6, _toDisplayString(searchQuery.value.trim() ? 'Try a different search term.' : 'Upload your first document to get started.'), 1)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredDocuments.value, (doc) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: doc.id,
                  class: "relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-5 flex flex-col"
                }, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("h3", _hoisted_9, _toDisplayString(doc.title), 1),
                    _createElementVNode("span", {
                      class: _normalizeClass(["shrink-0 px-2.5 py-0.5 text-xs font-semibold rounded-full", doc.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'])
                    }, _toDisplayString(doc.status), 3)
                  ]),
                  _createElementVNode("p", _hoisted_10, " Uploaded " + _toDisplayString(new Date(doc.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })), 1),
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("button", {
                      type: "button",
                      onClick: ($event: any) => (viewDocument(doc)),
                      class: "p-2 rounded-lg text-blue-600 hover:bg-blue-600/10 dark:hover:bg-primary-dark/10 transition-all",
                      title: "View Document"
                    }, [
                      _createVNode(_unref(Eye), { class: "h-5 w-5" })
                    ], 8, _hoisted_12),
                    _createElementVNode("button", {
                      type: "button",
                      onClick: ($event: any) => (downloadDocument(doc)),
                      class: "p-2 rounded-lg text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all",
                      title: "Download Document"
                    }, [
                      _createVNode(_unref(DownloadIcon), { class: "h-5 w-5" })
                    ], 8, _hoisted_13),
                    _createElementVNode("button", {
                      type: "button",
                      onClick: ($event: any) => (openEditModal(doc)),
                      class: "p-2 rounded-lg text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all",
                      title: "Edit Document"
                    }, [
                      _createVNode(_unref(Pencil), { class: "h-5 w-5" })
                    ], 8, _hoisted_14),
                    _createElementVNode("button", {
                      type: "button",
                      onClick: ($event: any) => (handleDelete(doc.id)),
                      class: "p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all",
                      title: "Delete Document"
                    }, [
                      _createVNode(_unref(Trash), { class: "h-5 w-5" })
                    ], 8, _hoisted_15)
                  ])
                ]))
              }), 128))
            ])),
    _createVNode(Modal, {
      modelValue: showDeleteModal.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showDeleteModal).value = $event)),
      title: "Delete Document"
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (showDeleteModal.value = false))
        }, " Cancel "),
        _createElementVNode("button", {
          type: "button",
          class: "ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 shadow-sm transition disabled:opacity-60",
          disabled: deleteLoading.value,
          onClick: confirmDelete
        }, _toDisplayString(deleteLoading.value ? 'Deleting...' : 'Yes, delete permanently'), 9, _hoisted_18)
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_16, [
          _cache[21] || (_cache[21] = _createTextVNode(" Are you sure you want to permanently delete ", -1)),
          _createElementVNode("span", _hoisted_17, "\"" + _toDisplayString(pendingDeleteTitle.value) + "\"", 1),
          _cache[22] || (_cache[22] = _createTextVNode("? This action cannot be undone. ", -1))
        ])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(Modal, {
      modelValue: showUploadModal.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showUploadModal).value = $event)),
      title: "Upload Document"
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 border rounded-lg dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700",
          onClick: _cache[8] || (_cache[8] = ($event: any) => (showUploadModal.value = false))
        }, " Cancel "),
        _createElementVNode("button", {
          type: "button",
          class: "ml-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary text-sm font-semibold disabled:opacity-50",
          disabled: uploading.value,
          onClick: uploadDocument
        }, _toDisplayString(uploading.value ? 'Uploading...' : 'Upload'), 9, _hoisted_24)
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_19, [
          _createElementVNode("div", null, [
            _cache[23] || (_cache[23] = _createElementVNode("label", { class: "label" }, [
              _createTextVNode("Document Title "),
              _createElementVNode("span", { class: "text-danger" }, "*")
            ], -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((uploadTitle).value = $event)),
              type: "text",
              class: "input",
              placeholder: "e.g. CPR Certification"
            }, null, 512), [
              [_vModelText, uploadTitle.value]
            ])
          ]),
          _createElementVNode("div", {
            class: _normalizeClass(["flex items-center gap-4 w-full rounded-2xl border-2 border-dashed p-6 cursor-pointer select-none", dragOver.value ? 'bg-primary/5 rounded-xl p-2 -m-2' : '']),
            onClick: triggerFileInput,
            onDragover: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (dragOver.value = true), ["prevent"])),
            onDragleave: _cache[7] || (_cache[7] = _withModifiers(($event: any) => (dragOver.value = false), ["prevent"])),
            onDrop: _withModifiers(onDrop, ["prevent"])
          }, [
            _createElementVNode("div", _hoisted_20, [
              _createVNode(_unref(Upload), { class: "h-5 w-5" })
            ]),
            _createElementVNode("div", _hoisted_21, [
              (selectedFileName.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("p", {
                      class: "text-sm font-semibold text-gray-800 dark:text-gray-100 truncate",
                      title: selectedFileName.value
                    }, _toDisplayString(selectedFileName.value), 9, _hoisted_22),
                    _cache[24] || (_cache[24] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400" }, " Click or drop to change file ", -1))
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _cache[25] || (_cache[25] = _createElementVNode("p", { class: "text-sm font-semibold text-gray-800 dark:text-gray-100" }, " Drag & drop your file here ", -1)),
                    _cache[26] || (_cache[26] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400" }, " or click to browse ", -1))
                  ], 64))
            ]),
            _createElementVNode("input", {
              ref_key: "fileInputRef",
              ref: fileInputRef,
              type: "file",
              class: "hidden",
              onChange: handleFileChange
            }, null, 544)
          ], 34),
          (uploadError.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_23, _toDisplayString(uploadError.value), 1))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(Modal, {
      modelValue: showEditModal.value,
      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((showEditModal).value = $event)),
      title: "Edit Document"
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 border rounded-lg dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700",
          onClick: _cache[14] || (_cache[14] = ($event: any) => (showEditModal.value = false))
        }, "Cancel"),
        _createElementVNode("button", {
          type: "button",
          class: "ml-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary text-sm font-semibold disabled:opacity-50",
          disabled: editLoading.value,
          onClick: confirmEdit
        }, _toDisplayString(editLoading.value ? 'Saving...' : 'Save'), 9, _hoisted_30)
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_25, [
          _createElementVNode("div", null, [
            _cache[27] || (_cache[27] = _createElementVNode("label", { class: "label" }, [
              _createTextVNode("Document Title "),
              _createElementVNode("span", { class: "text-danger" }, "*")
            ], -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((editTitle).value = $event)),
              type: "text",
              class: "input",
              placeholder: "e.g. CPR Certification"
            }, null, 512), [
              [_vModelText, editTitle.value]
            ])
          ]),
          _createElementVNode("div", null, [
            _cache[31] || (_cache[31] = _createElementVNode("label", { class: "label" }, "File (leave empty to keep current)", -1)),
            _createElementVNode("div", {
              class: _normalizeClass(["flex items-center gap-4 w-full rounded-2xl border-2 border-dashed p-6 cursor-pointer select-none", editDragOver.value ? 'bg-primary/5' : '']),
              onClick: _cache[11] || (_cache[11] = ($event: any) => (editFileInputRef.value?.click())),
              onDragover: _cache[12] || (_cache[12] = _withModifiers(($event: any) => (editDragOver.value = true), ["prevent"])),
              onDragleave: _cache[13] || (_cache[13] = _withModifiers(($event: any) => (editDragOver.value = false), ["prevent"])),
              onDrop: _withModifiers(onEditDrop, ["prevent"])
            }, [
              _createElementVNode("div", _hoisted_26, [
                _createVNode(_unref(Upload), { class: "h-5 w-5" })
              ]),
              _createElementVNode("div", _hoisted_27, [
                (editFile.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("p", {
                        class: "text-sm font-semibold text-gray-800 dark:text-gray-100 truncate",
                        title: editFile.value.name
                      }, _toDisplayString(editFile.value.name), 9, _hoisted_28),
                      _cache[28] || (_cache[28] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400" }, "Click or drop to change file", -1))
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _cache[29] || (_cache[29] = _createElementVNode("p", { class: "text-sm font-semibold text-gray-800 dark:text-gray-100" }, "Keep current file", -1)),
                      _cache[30] || (_cache[30] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400" }, "or click to browse for a new file", -1))
                    ], 64))
              ]),
              _createElementVNode("input", {
                ref_key: "editFileInputRef",
                ref: editFileInputRef,
                type: "file",
                class: "hidden",
                onChange: onEditFileChange
              }, null, 544)
            ], 34)
          ]),
          (editError.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_29, _toDisplayString(editError.value), 1))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(DocumentDetailsModal, {
      "is-open": showViewModal.value,
      document: selectedDocument.value,
      onClose: _cache[16] || (_cache[16] = ($event: any) => {showViewModal.value = false; selectedDocument.value = null})
    }, null, 8, ["is-open", "document"])
  ]))
}
}

})